// Core game logic - platform independent (ported from ios/RingenGame.swift)

import type { Level, Tile, Move, Palette } from './types'

export class RingenGame {
  board: (Tile | null)[]
  moveCount: number = 0

  readonly width: number
  readonly height: number
  readonly disabledIndices: Set<number>
  readonly numTiles: number
  readonly moves: Move[]
  readonly palette: Palette
  readonly configNames: string[]
  readonly numOrientationsMap: Record<string, number>

  readonly goalBoard: (Tile | null)[]
  private readonly initialBoard: (Tile | null)[]
  private readonly compactToFull: number[]

  // Legacy properties for compatibility
  get rows(): number {
    return this.height
  }
  get cols(): number {
    return this.width
  }

  constructor(level: Level) {
    this.width = level.width
    this.height = level.height
    this.disabledIndices = new Set(level.disabled_indices)
    this.numTiles = level.num_tiles
    this.moves = level.moves
    this.palette = level.palette
    this.configNames = level.config_names
    this.numOrientationsMap = level.num_orientations_map

    // Build mapping from compact index to full board index
    this.compactToFull = RingenGame.buildCompactToFullMapping(
      level.width,
      level.height,
      new Set(level.disabled_indices)
    )

    // Create goal board from multi-valued goal state with orientations
    const goal: (Tile | null)[] = new Array(this.width * this.height).fill(null)
    for (let compactIdx = 0; compactIdx < level.num_tiles; compactIdx++) {
      const fullIdx = this.compactToFull[compactIdx]
      const values = level.goal_state[compactIdx]
      const orientations =
        level.goal_orientations.length === 0
          ? new Array(values.length).fill(0)
          : level.goal_orientations[compactIdx]

      // Use first config value for color/symbol lookup
      const displayValue = values[0]
      const entry = level.palette.mappings[String(displayValue)]
      goal[fullIdx] = {
        values,
        orientations,
        color: entry?.color ?? '#808080',
        symbol: entry?.symbol ?? '',
        rotationSymmetrySteps: entry?.rotation_symmetry_steps,
      }
    }
    this.goalBoard = goal

    // Create initial board from starting state with orientations
    const initial: (Tile | null)[] = new Array(this.width * this.height).fill(null)
    for (let compactIdx = 0; compactIdx < level.num_tiles; compactIdx++) {
      const fullIdx = this.compactToFull[compactIdx]
      const values = level.starting_state[compactIdx]
      const orientations =
        level.starting_orientations.length === 0
          ? new Array(values.length).fill(0)
          : level.starting_orientations[compactIdx]

      const displayValue = values[0]
      const entry = level.palette.mappings[String(displayValue)]
      initial[fullIdx] = {
        values,
        orientations,
        color: entry?.color ?? '#808080',
        symbol: entry?.symbol ?? '',
        rotationSymmetrySteps: entry?.rotation_symmetry_steps,
      }
    }
    this.initialBoard = initial
    this.board = [...initial] // Copy array
  }

  // Build mapping from compact index (0 to numTiles-1) to full board index
  private static buildCompactToFullMapping(
    width: number,
    height: number,
    disabledIndices: Set<number>
  ): number[] {
    const mapping: number[] = []
    for (let fullIdx = 0; fullIdx < width * height; fullIdx++) {
      if (!disabledIndices.has(fullIdx)) {
        mapping.push(fullIdx)
      }
    }
    return mapping
  }

  // Convert compact index (0 to numTiles-1) to full board index (0 to width*height-1)
  fullIndex(compactIndex: number): number {
    if (compactIndex < 0 || compactIndex >= this.compactToFull.length) {
      return compactIndex // Fallback for out of range
    }
    return this.compactToFull[compactIndex]
  }

  // Convert full board index to compact index (-1 if disabled/not found)
  compactIndex(fullIndex: number): number {
    return this.compactToFull.indexOf(fullIndex)
  }

  isSolved(): boolean {
    // Check each tile for both position (values) and orientation match
    for (let i = 0; i < this.board.length; i++) {
      const tile = this.board[i]
      const goal = this.goalBoard[i]

      // Both must be null or both must be non-null
      if ((tile === null) !== (goal === null)) {
        return false
      }

      if (tile && goal) {
        // Check values match (position)
        if (!this.arraysEqual(tile.values, goal.values)) {
          return false
        }

        // Check orientation (for orientable tiles)
        const tileValue = tile.values[0]
        const numOrients = this.numOrientationsMap[String(tileValue)] ?? 1
        if (numOrients > 1 && !this.arraysEqual(tile.orientations, goal.orientations)) {
          return false
        }
      }
    }

    return true
  }

  private arraysEqual(a: number[], b: number[]): boolean {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false
    }
    return true
  }

  reset(): void {
    this.board = [...this.initialBoard] // Deep copy
    this.moveCount = 0
  }

  setToGoalState(): void {
    this.board = [...this.goalBoard] // Deep copy
    // Keep existing moveCount - don't reset it
  }

  // Apply a dynamic (ouroboros) move from a cycle of compact indices
  applyDynamicMove(compactCycle: number[], clockwise: boolean = true): void {
    if (compactCycle.length < 2) return

    const fullCycle = compactCycle.map((idx) => this.fullIndex(idx))
    let tempTiles = fullCycle.map((idx) => this.board[idx])

    // Permute tiles (no orientation deltas for dynamic moves)
    if (clockwise) {
      const last = tempTiles[tempTiles.length - 1]
      for (let i = tempTiles.length - 1; i >= 1; i--) {
        tempTiles[i] = tempTiles[i - 1]
      }
      tempTiles[0] = last
    } else {
      const first = tempTiles[0]
      for (let i = 0; i < tempTiles.length - 1; i++) {
        tempTiles[i] = tempTiles[i + 1]
      }
      tempTiles[tempTiles.length - 1] = first
    }

    const newBoard = [...this.board]
    for (let i = 0; i < fullCycle.length; i++) {
      newBoard[fullCycle[i]] = tempTiles[i]
    }

    this.board = newBoard
    this.moveCount += 1
  }

  // Apply a predefined move by index
  applyMove(moveIndex: number, clockwise: boolean = true): void {
    if (moveIndex < 0 || moveIndex >= this.moves.length) return

    const move = this.moves[moveIndex]
    const compactCycle = move.cycle // These are compact indices
    const deltas = move.orientation_deltas

    if (compactCycle.length < 2) return

    // Convert compact indices to full board indices
    const fullCycle = compactCycle.map((idx) => this.fullIndex(idx))

    // Extract tiles from board
    let tempTiles = fullCycle.map((idx) => this.board[idx])

    // CRITICAL FIX: Apply orientation deltas BEFORE permuting positions
    // The delta at index i applies to the tile currently at cycle[i] (before move)
    if (deltas.length > 0) {
      // HACK: When going counter-clockwise, negate all deltas
      // This is a temporary workaround until server sends separate CW/CCW moves
      // NOTE: Only works correctly for uniform deltas (e.g., [1,1,1,1])
      const effectiveDeltas = clockwise ? deltas : deltas.map((d) => -d)

      for (let i = 0; i < tempTiles.length; i++) {
        const tile = tempTiles[i]
        if (!tile) continue

        // Get num_orientations for this tile value (use first config)
        const tileValue = tile.values[0]
        const numOrients = this.numOrientationsMap[String(tileValue)] ?? 1

        // Apply delta at this position (tile hasn't moved yet)
        const delta = effectiveDeltas[i]

        // Apply delta to all orientation configs if tile is orientable
        if (numOrients > 1 && delta !== 0) {
          tile.orientations = tile.orientations.map((orient) =>
            ((orient + delta) % numOrients + numOrients) % numOrients // Handle negative modulo
          )
        }

        tempTiles[i] = tile
      }
    }

    // Now permute the tiles (positions permute, orientations travel with tiles)
    if (clockwise) {
      // Rotate right: last element goes to front
      const last = tempTiles[tempTiles.length - 1]
      for (let i = tempTiles.length - 1; i >= 1; i--) {
        tempTiles[i] = tempTiles[i - 1]
      }
      tempTiles[0] = last
    } else {
      // Rotate left: first element goes to end
      const first = tempTiles[0]
      for (let i = 0; i < tempTiles.length - 1; i++) {
        tempTiles[i] = tempTiles[i + 1]
      }
      tempTiles[tempTiles.length - 1] = first
    }

    // Write back to board
    const newBoard = [...this.board]
    for (let i = 0; i < fullCycle.length; i++) {
      newBoard[fullCycle[i]] = tempTiles[i]
    }

    this.board = newBoard
    this.moveCount += 1
  }
}
